import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  input,
  output,
  signal,
  viewChild,
  viewChildren,
  type WritableSignal,
} from '@angular/core';
import { Search } from '@local/client-contracts';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ResultCommandService } from 'src/app/bar/services/commands/result-command.service';
import { InvokeCommand } from '../../models/invoke-command.model';
import { windowSizeObserver } from '@shared/utils';
export type ResultsWidth = 'large' | 'medium' | 'small' | 'extra-small';

@UntilDestroy()
@Component({
  selector: 'short-result-resources',
  templateUrl: './short-result-resources.component.html',
  styleUrls: ['./short-result-resources.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ShortResultResourcesComponent {
  private readonly ADDITIONAL_RESOURCES_WIDTH = 101;
  private readonly TEXT_LENGTH_MULTIPLIER = 6;
  private readonly MAX_WIDTH_FOR_SEE_All = 279;

  private showAllResources = signal<boolean>(false);

  resources = input<Search.ResultItem[]>([]);
  resourceActiveIndex = input<number>(0);

  searchId = input<string>();
  sessionId = input<string>();
  displayHeader = input<boolean>(false);
  showResourcesNumbers = input<boolean>(false);

  resourcesElements = viewChildren('shortResultResource');
  wrapper = viewChild<ElementRef>('wrapper');

  showAll = computed(() => {
    return this.showAllResources() || this.limit() === null;
  });
  sliceResources = computed(() => {
    return this.showAll() ? this.resources() : this.resources().slice(0, this.limit());
  });

  additionalResources = computed(() => {
    return this.showAll() ? null : this.resources()?.slice(this.limit());
  });

  limit: WritableSignal<number> = signal(null);
  seeAll: WritableSignal<boolean> = signal(false);
  hasTwoResourcesOverflow: WritableSignal<boolean> = signal(false);
  menuActive = output<boolean>();
  isFirstInit: boolean = true;

  constructor(private resultCommandService: ResultCommandService) {
    windowSizeObserver()
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        if (!this.isFirstInit) {
          this.limit.set(null);
          this.isFirstInit = true;
        }
      });

    effect(
      () => {
        const value = this.resourceActiveIndex();
        if (value > this.limit()) {
          this.onAdditionalResourcesClick(true);
        }
      },
      { allowSignalWrites: true }
    );

    effect(
      () => {
        this.calcLimit();
      },
      { allowSignalWrites: true }
    );
  }

  calcLimit() {
    if (this.showAllResources() || !this.resourcesElements()?.length || this.resources().length === 1 || !this.isFirstInit) {
      return;
    }

    this.isFirstInit = false;
    this.seeAll.set(false);
    this.limit.set(null);
    this.hasTwoResourcesOverflow.set(false);

    const totalWidth = this.wrapper()?.nativeElement.offsetWidth;

    if (totalWidth < this.MAX_WIDTH_FOR_SEE_All && this.resourcesElements().length >= 2) {
      this.seeAll.set(true);
      this.limit.set(0);
      return;
    }

    this.setResourceCountBySpace(totalWidth);
  }

  setResourceCountBySpace(totalWidth: number) {
    let total: number = 0;
    let count: number = 0;
    const elementsWidth: number[] = this.resourcesElements().map((itemRef: ElementRef) => {
      return itemRef.nativeElement.offsetWidth;
    });

    if (this.resourcesElements().length === 2 && elementsWidth[0] + elementsWidth[1] + this.TEXT_LENGTH_MULTIPLIER > totalWidth) {
      this.hasTwoResourcesOverflow.set(true);
      return;
    }

    for (const width of elementsWidth) {
      if (total + width < totalWidth) {
        total += width + this.TEXT_LENGTH_MULTIPLIER;
        count++;
      }
    }
    if (count < this.resources()?.length) {
      total += this.ADDITIONAL_RESOURCES_WIDTH;
      while (total > totalWidth) {
        total -= elementsWidth[count - 1] - this.TEXT_LENGTH_MULTIPLIER;
        count--;
      }
      if (count < 2) {
        count = 1;
        if (elementsWidth[0] + this.TEXT_LENGTH_MULTIPLIER + this.ADDITIONAL_RESOURCES_WIDTH > totalWidth) {
          this.hasTwoResourcesOverflow.set(true);
        }
      }
      return this.limit.set(count);
    }
  }

  isMenuActive(event: boolean) {
    this.menuActive.emit(event);
  }
  async onInvoke(data: InvokeCommand, type: Search.ResultType, index: number): Promise<void> {
    this.resultCommandService.onInvoke(
      this.resources(),
      data,
      type,
      this.sessionId(),
      null,
      (data.invokerItem as any).searchId || this.searchId(),
      index,
      0
    );
  }

  onAdditionalResourcesClick(event: boolean) {
    this.hasTwoResourcesOverflow.set(false);
    this.showAllResources.set(event);
  }
}
